import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Alert from '../../_shared/components/Alert';
import RetailService from '../../services/RetailService';
import Icon from '../../_shared/components/Icon';
import LightboxGallery from '../../components/LightboxGallery';
import PoweredBy from '../../_shared/components/PoweredBy';
import copyToClipBoard from '../../_shared/utils/copyToClipBoard';
import { isMobileDevice } from '../../_shared/utils/userAgent';
import Loading from '../../_shared/components/Loading';
import Carousel from './Carousel';
import VehicleHeader from './VehicleHeader';
import VehicleDetails from './VehicleDetails';
import SideRail from './SideRail';
import MixpanelService from '../../services/MixpanelService';
import mixpanelConfig from '../../_shared/constants/mixpanelConfig';
import IntercomService from '../../services/IntercomService';
import theme from '../../_shared/themes/tradeRev';

import {
  RetailPlatforms,
  RetailVehicleFeatures
} from '../../_shared/constants/retail';

import {
  Wrapper,
  RetailVdpContainer,
  CopyUrlButton,
  DetailsSection,
  Footer,
  FooterText,
  StyledRetailVdpErrorContainer,
  StyledCopyToClipboard
} from './Styles';
import TRToast from '../../_shared/helpers/TRToast';

const defaultPropTypes = {
  guid: PropTypes.string
};
const RetailVdp = ({ guid, isOmp }) => {
  const [retailTrade, setRetailTrade] = useState({});
  const [tiahToken, setTiahToken] = useState(retailTrade?.tiahToken);
  const [dealer, setDealer] = useState(retailTrade?.dealer);
  const [metadata, setMetadata] = useState(retailTrade?.retailTradeMetadata);
  const [tradeDetails, setTradeDetails] = useState(retailTrade?.tradeDetails);
  const [media, setMedia] = useState(retailTrade?.media);
  const [features, setFeatures] = useState(retailTrade?.features || []);
  const [mileage, setMileage] = useState(retailTrade?.mileage);
  const [dealerLogoSrc, setDealerLogoSrc] = useState(
    retailTrade?.dealerLogoSrc
  );
  const [mpTracked, setMpTracked] = useState(0);

  const mixpanel = MixpanelService.get();

  const onCopyClick = () => {
    const url = window.location.href;
    const user = JSON.parse(window.localStorage.getItem('auth'))?.user;

    let isShare = false;
    if (isMobileDevice() && navigator.share) {
      navigator.share({ url });
      isShare = true;
    } else {
      copyToClipBoard(url);

      TRToast(
        <Alert
          variant="success"
          messages={[<FormattedMessage id="retail.url.copied" />]}
        />
      );
    }

    if (retailTrade?.guid && tradeDetails) {
      mixpanel.track(mixpanelConfig.names.shareRetailVdp, {
        retail_guid: retailTrade.guid,
        year: tradeDetails.year,
        make: tradeDetails.make,
        model: tradeDetails.model,
        vin: tradeDetails.vin,
        trim: tradeDetails.trim,
        platform: isMobileDevice()
          ? RetailPlatforms.Mobile
          : RetailPlatforms.Web,
        is_retail_customer: user === undefined,
        is_sharebox_populated: isShare,
        dealer: dealer?.displayName,
        dealer_id: null // overrides superproperty because superproperty will capture the logged in user and not the Retail VDP creator
      });
    }
  };

  useEffect(() => {
    const fetchRetailDetails = async () => {
      const trade = await RetailService.getRetailCase(guid);
      setTiahToken(trade?.tiahToken);
      setDealer(trade?.dealer);
      setMetadata(trade?.retailTradeMetadata);
      setTradeDetails(trade?.tradeDetails);
      setMedia(trade?.media);
      setRetailTrade(trade);
      setFeatures(trade?.features);
      setMileage(trade?.mileage);
      setDealerLogoSrc(trade?.dealerLogoSrc);
    };

    if (retailTrade?.guid !== guid) {
      fetchRetailDetails();
    }
  }, [guid, retailTrade?.guid]);

  useEffect(() => {
    if (
      tradeDetails &&
      Object.keys(tradeDetails).length &&
      mpTracked === 0 &&
      retailTrade?.guid
    ) {
      mixpanel.track(mixpanelConfig.names.accessRetailVdp, {
        retail_guid: retailTrade?.guid,
        year: tradeDetails.year,
        make: tradeDetails.make,
        model: tradeDetails.model,
        vin: tradeDetails.vin,
        trim: tradeDetails.trim,
        platform: isMobileDevice()
          ? RetailPlatforms.Mobile
          : RetailPlatforms.Web,
        is_retail_customer:
          JSON.parse(window.localStorage.getItem('auth'))?.user === undefined,
        vehicle_features:
          features === []
            ? RetailVehicleFeatures.CARRYOVER
            : RetailVehicleFeatures.DECODED,
        dealer: dealer?.displayName
      });
      setMpTracked(1);

      IntercomService.trackEvent('View retail VDP', {
        dealerCanSell: tiahToken?.length > 0
      });
    }
  }, [
    mixpanel,
    mpTracked,
    retailTrade?.guid,
    tradeDetails,
    features,
    dealer?.displayName,
    tiahToken
  ]);
  const [isMobile, setIsMobile] = useState(
    window.matchMedia(`(max-width: ${theme.BP.VDP_MOBILE_WH})`)
  );

  useEffect(() => {
    window.addEventListener('resize', () =>
      setIsMobile(window.matchMedia(`(max-width: ${theme.BP.VDP_MOBILE_WH})`))
    );
    return () =>
      window.removeEventListener('resize', () =>
        setIsMobile(window.matchMedia(`(max-width: ${theme.BP.VDP_MOBILE_WH})`))
      );
  }, []);

  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [selectedPhotoIdx, setSelectedPhotoIdx] = useState(null);

  const onGalleryOpen = selectedIndex => {
    setSelectedPhotoIdx(selectedIndex);
    setIsGalleryOpen(true);
  };

  return retailTrade === {} || retailTrade === undefined ? (
    <StyledRetailVdpErrorContainer>
      <Icon icon="TR5-Warning_msg" />
      <FormattedMessage id="retail.error" />
    </StyledRetailVdpErrorContainer>
  ) : (
    <Wrapper>
      {tradeDetails && (
        <StyledCopyToClipboard onClick={onCopyClick} icon="TR5-Share">
          <CopyUrlButton>
            <FormattedMessage id="vdp.retail.shareUrl" />
          </CopyUrlButton>
        </StyledCopyToClipboard>
      )}
      <RetailVdpContainer>
        <Carousel
          media={media}
          isGalleryOpen={isGalleryOpen}
          onGalleryOpen={onGalleryOpen}
          retailTrade={retailTrade}
          tradeDetails={tradeDetails}
          dealerName={dealer?.displayName}
        />
        {tradeDetails ? (
          <DetailsSection>
            <VehicleHeader
              tradeDetails={tradeDetails}
              mileage={mileage}
              isOmp={isOmp}
            />
            {dealer ? (
              isMobile?.matches && (
                <SideRail
                  tiahToken={tiahToken}
                  dealer={dealer}
                  metadata={metadata}
                  guid={retailTrade?.guid}
                  tradeDetails={tradeDetails}
                  dealerLogoSrc={dealerLogoSrc}
                  isOmp={isOmp}
                />
              )
            ) : (
              <Loading width="500px" height="200px" />
            )}
            <VehicleDetails
              tradeDetails={tradeDetails}
              metadata={metadata}
              features={features}
            />
          </DetailsSection>
        ) : (
          <DetailsSection>
            <Loading width="500px" height="100px" />
          </DetailsSection>
        )}
        {dealer ? (
          !isMobile?.matches && (
            <SideRail
              tiahToken={tiahToken}
              dealer={dealer}
              metadata={metadata}
              guid={retailTrade?.guid}
              tradeDetails={tradeDetails}
              dealerLogoSrc={dealerLogoSrc}
              isOmp={isOmp}
            />
          )
        ) : (
          <Loading width="500px" height="200px" />
        )}
      </RetailVdpContainer>
      <Footer>
        {!isOmp && <PoweredBy />}
        <FooterText>
          <FormattedMessage id="retail.footer" />
        </FooterText>
      </Footer>
      {!isMobileDevice() && (
        <LightboxGallery
          isOpen={isGalleryOpen}
          setIsGalleryOpen={setIsGalleryOpen}
          media={media}
          selectedPhotoIdx={selectedPhotoIdx}
          retailTrade={retailTrade}
          tradeDetails={tradeDetails}
          dealerName={dealer?.displayName}
        />
      )}
    </Wrapper>
  );
};
RetailVdp.defaultProps = defaultPropTypes;
export default RetailVdp;
